import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/common";
import { DocLinks } from "@/components/PracticalDocs";
import BookingEditDialog from "@/components/BookingEditDialog";
import { PrintButton } from "@/components/PrintButton";
import { api, errMsg, fmtDate, STATUSES } from "@/lib/api";
import { printBookingSheet } from "@/lib/print";
import { RECORDED_SHORTAGES_ENABLED } from "@/lib/features";

function ChecklistItem({ c, bookingId, editable, onChange }) {
  const [missing, setMissing] = useState("");
  const [note, setNote] = useState("");
  const [recordingShortage, setRecordingShortage] = useState(false);
  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/bookings/${bookingId}/checklist/${c.id}`, body);
      onChange(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const startShortage = () => {
    setMissing(String(c.missing_qty || c.required || ""));
    setRecordingShortage(true);
  };
  const recordShortage = () => {
    const quantity = Number(missing);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Enter the amount that is short.");
      return;
    }
    patch({ missing_qty: quantity, note });
    setMissing("");
    setNote("");
    setRecordingShortage(false);
  };
  return (
    <li className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 border-b last:border-0" data-testid={`checklist-item-${c.id}`}>
      <label className="flex items-center gap-2.5 flex-1 cursor-pointer">
        <Checkbox checked={c.done} disabled={!editable} onCheckedChange={(v) => patch({ done: !!v })} data-testid={`checklist-checkbox-${c.id}`} />
        <span className={`text-sm ${c.done ? "line-through text-muted-foreground" : ""}`}>{c.item}</span>
        <span className="font-mono text-xs text-muted-foreground">{c.required} {c.units}</span>
      </label>
      {RECORDED_SHORTAGES_ENABLED && c.missing_qty > 0 && (
        <span className="text-xs text-destructive">{c.missing_qty} missing</span>
      )}
      {RECORDED_SHORTAGES_ENABLED && editable && (
        recordingShortage ? (
          <div className="flex flex-wrap gap-1">
            <Input
              type="number"
              min={0.01}
              step="any"
              aria-label={`Amount of ${c.item} short`}
              placeholder="Amount short"
              value={missing}
              onChange={(event) => setMissing(event.target.value)}
              className="h-8 w-28 text-xs"
              data-testid={`checklist-missing-input-${c.id}`}
            />
            <Input
              aria-label={`Shortage note for ${c.item}`}
              placeholder="Optional note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="h-8 w-36 text-xs"
              data-testid={`checklist-shortage-note-${c.id}`}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={recordShortage}
              data-testid={`checklist-missing-save-${c.id}`}
            >
              Save shortage
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setRecordingShortage(false)}
              data-testid={`checklist-shortage-cancel-${c.id}`}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={startShortage}
            data-testid={`checklist-record-shortage-${c.id}`}
          >
            <AlertTriangle className="mr-1.5 h-3.5 w-3.5" />
            {c.missing_qty > 0 ? "Update shortage" : "Record shortage"}
          </Button>
        )
      )}
    </li>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm font-medium">{value || "—"}</div></div>
);

export default function BookingDetail({
  bookingId,
  onClose,
  mode,
  onChanged,
  onRepeatForLesson,
  technicians = [],
}) {
  const { user, can } = useAuth();
  const [b, setB] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setB(null);
    if (bookingId) {
      api.get(`/bookings/${bookingId}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
    }
  }, [bookingId]);
  const update = (data) => {
    setB((old) => ({ ...data, history: old?.history }));
    api.get(`/bookings/${data.id}`).then((r) => setB(r.data)).catch(() => {});
    onChanged?.();
  };
  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/status`, { status });
      update(data);
      toast.success(`Status set to ${status}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const assign = async (tid) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/assign`, { technician_id: tid === "none" ? null : tid });
      update(data);
      toast.success(data.assigned_to ? `Assigned to ${data.assigned_to.name}` : "Unassigned");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async () => {
    try {
      if (tech) {
        const { data } = await api.post(`/bookings/${bookingId}/status`, { status: "Cancelled" });
        update(data);
      } else {
        await api.post(`/bookings/${bookingId}/cancel`, { reason: "Cancelled from bookings page" });
        const { data } = await api.get(`/bookings/${bookingId}`);
        update(data);
      }
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tech = mode === "tech";
  const done = b ? b.checklist.filter((c) => c.done).length : 0;
  const canEditBooking = b && !["Cancelled", "Completed"].includes(b.status)
    && (b.teacher_id === user.id || can("booking:prep"));
  const canRepeatForLesson = b && b.teacher_id !== user.id && can("booking:create");
  return (
    <Sheet open={!!bookingId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="booking-detail-sheet">
        {!b ? <p className="text-sm text-muted-foreground p-4">Loading…</p> : (
          <div className="space-y-6">
            <SheetHeader>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground" data-testid="booking-detail-reference">
                  {b.reference}
                </span>
                <StatusBadge status={b.status} testId="booking-detail-status" />
                <PrintButton
                  label="Print booking"
                  size="sm"
                  onClick={() => printBookingSheet(b)}
                  testId="booking-detail-print-button"
                />
              </div>
              <SheetTitle className="text-xl text-left">{b.practical.name}</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Info label="Teacher" value={b.teacher_name} />
              <Info label="Class" value={`${b.lesson.class_name} (${b.lesson.pupils})`} />
              <Info label="Room" value={b.lesson.lab} />
              <Info label="Date" value={`${fmtDate(b.lesson.date)} · Week ${b.lesson.week}`} />
              <Info
                label="Period"
                value={`${(b.lesson.periods || [b.lesson.period]).map((period) => `P${period}`).join(" & ")} ${b.lesson.start}–${b.lesson.end}`}
              />
              <Info label="Sets" value={b.sets} />
            </div>
            {b.practical.prep_notes && <p className="text-sm"><span className="font-semibold">Prep notes: </span>{b.practical.prep_notes}</p>}
            {b.notes && <p className="text-sm"><span className="font-semibold">Teacher notes: </span>{b.notes}</p>}
            {canEditBooking && (
              <Button
                onClick={() => setEditing(true)}
                size="sm"
                variant="outline"
                data-testid="booking-edit-button"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit booking
              </Button>
            )}
            {canRepeatForLesson && (
              <Button
                onClick={() => onRepeatForLesson?.(b)}
                size="sm"
                variant="outline"
                data-testid="booking-repeat-for-my-lesson-button"
              >
                <Copy className="mr-2 h-4 w-4" />
                Repeat for my lesson
              </Button>
            )}
            {b.documents?.length > 0 && <div><h3 className="font-semibold text-sm mb-2">Risk assessments & worksheets</h3><DocLinks docs={b.documents} testId="booking-docs" /></div>}
            {tech && (
              <div className="grid grid-cols-[72px_1fr] items-center gap-3 max-w-md">
                <span className="text-sm font-medium">Status</span>
                <Select value={b.status} onValueChange={setStatus}>
                  <SelectTrigger className="w-full" data-testid={`technician-status-select-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.filter((status) => status !== "Cancelled").map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.replace(" ", "-").toLowerCase()}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-sm font-medium">Assigned</span>
                <Select value={b.assigned_to?.id || "none"} onValueChange={assign}>
                  <SelectTrigger className="w-full" data-testid={`technician-assign-select-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="assign-option-none">Unassigned</SelectItem>
                    {b.assigned_to && !technicians.some((t) => t.id === b.assigned_to.id) && <SelectItem value={b.assigned_to.id}>{b.assigned_to.name}</SelectItem>}
                    {technicians.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`assign-option-${t.id}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!tech && b.assigned_to && <p className="text-sm" data-testid="booking-detail-assignee"><span className="font-semibold">Technician: </span>{b.assigned_to.name}</p>}
            <div>
              <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-sm">Preparation checklist</h3><span className="text-xs text-muted-foreground" data-testid="checklist-progress">{done}/{b.checklist.length}</span></div>
              <Progress value={b.checklist.length ? (done / b.checklist.length) * 100 : 0} className="h-1.5 mb-2" />
              {RECORDED_SHORTAGES_ENABLED && tech && !["Cancelled", "Completed"].includes(b.status) && (
                <p className="mb-2 text-xs text-muted-foreground" data-testid="checklist-shortage-help">
                  Use Record shortage when an item is unavailable or there is not enough for this booking.
                </p>
              )}
              <ul>{b.checklist.map((c) => <ChecklistItem key={c.id} c={c} bookingId={b.id} editable={tech && !["Cancelled", "Completed"].includes(b.status)} onChange={update} />)}</ul>
            </div>
            {RECORDED_SHORTAGES_ENABLED && b.shortages?.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1" data-testid="booking-shortages">
                {b.shortages.map((s) => <p key={s.id} className="text-xs flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" />{s.qty} × {s.item} short {s.note && `— ${s.note}`} ({s.recorded_by})</p>)}
              </div>
            )}
            {!['Cancelled', 'Completed'].includes(b.status) && (tech || b.teacher_id === user.id || can("booking:cancel_any")) && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-destructive" data-testid="booking-detail-cancel-button">Cancel booking</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Cancel {b.reference}?</AlertDialogTitle><AlertDialogDescription>The technicians will be notified. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep booking</AlertDialogCancel><AlertDialogAction onClick={cancel} data-testid="booking-detail-cancel-confirm">Cancel booking</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {b.history?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">History</h3>
                <ul className="space-y-1 text-xs text-muted-foreground">{b.history.map((h) => <li key={h.id}>{new Date(h.created_at).toLocaleString("en-GB")} — {h.user_name}: {h.action}{h.details?.to ? ` → ${h.details.to}` : ""}{h.details?.item ? ` (${h.details.item})` : ""}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </SheetContent>
      {b && (
        <BookingEditDialog
          booking={b}
          open={editing}
          onOpenChange={setEditing}
          onSaved={update}
        />
      )}
    </Sheet>
  );
}
